import { useAvatarAnimator } from '../avatar/useAvatarAnimator';
import React, { useRef, useEffect, useMemo, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { useAvatarModel } from '../avatar/vrm/useAvatarModel';
import { isVrmUrl } from '../avatar/vrm/vrmFormat';
import VrmAvatarRuntime from './vrm/VrmAvatarRuntime';
import { createVrmGroundProbe } from '../avatar/vrm/vrmGround';
import { useLookTexture } from '../utils/useLookTexture';
import * as THREE from 'three';
import { isTripoRig } from '../avatar/rig';
import { repairHeadArmWeights } from '../avatar/skinRepair';
import { readAvatarMeta, nameTagHeight, eyeHeight, lookHeight, HOVER } from '../avatar/avatarMeta';
import { seatTableTop } from './games/tableLayout';
import { TABLE_EYE } from './VR/seatedMode';
import { retargetClipToRig, isTripoLibraryClip, fitLibraryClip, captureRest } from '../avatar/libraryRetarget';
import { clone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import FloatingNameTagWithEvents from './FloatingNameTagWithEvents';
import GhostAvatar from './GhostAvatar';
import { useWorldStore } from '../stores/worldStore';
import { clampToBounds, insideBounds } from '../utils/worldBounds';
import { ARM_MARGIN, ARM_MIN, FLOOR_CLEARANCE, blocksCamera, clampCamera, coversXZ, isOverhead, isSlab, nearBox, rayBoxDistance, rayExitBounds, shortenArm, type CameraClamp } from '../utils/cameraArm';
import { ChairSitFitter, chairSeatFromMarker, findChairMarker, type ChairSeat } from '../avatar/chairSit';
import { usePortalArrival } from './portals/portals';
import { EMOTES, EMOTE_MAP, TRIPO_EQUIVALENT, LIBRARY_SOURCE_MODEL, SIT_EMOTES, SEAT_CLEARANCE, FLOOR_PERCH, CHAIR_POSE_HIP, CHAIR_POSE_HIP_FWD, CHAIR_POSE_FOOT, CHAIR_HIP_CLEARANCE, CHAIR_FEET_SINK, CHAIR_HIP_AHEAD, getSitStyle, sitStyleFor } from '../config/emotes';
import { getDRACOLoader } from '../utils/loaderConfig';
import { broadcastEmote } from '../services/worldBroadcast';
import { getCurrentWorldId } from '../services/realtimeManager';
import { leaveLocalPose, takeLocalPose } from '../avatar/carryPose';
import { takeTouchLook, touchMove, touchMoveAmount } from './mobile/touchControls';

/** Frame-rate-independent smoothing factor: fraction to move toward target
 *  this frame so the response is consistent regardless of FPS. */
function smoothFactor(rate: number, delta: number): number {
  return 1 - Math.exp(-rate * delta);
}

/** Lerp an angle toward a target along the shortest arc. */
function lerpAngle(current: number, target: number, t: number): number {
  let diff = target - current;
  diff = Math.atan2(Math.sin(diff), Math.cos(diff));
  return current + diff * t;
}

/** Check if a URL points to a defunct/unreachable/expired domain */
function isDeadAvatarUrl(url: string): boolean {
  if (!url || url === 'skip') return true;
  // Ready Player Me shut down Jan 31 2026
  if (url.includes('readyplayer.me')) return true;
  // Meshy signed URLs are temporary — check if expired
  if (url.includes('assets.meshy.ai')) {
    const expiresMatch = url.match(/Expires=(\d+)/);
    if (expiresMatch) {
      const expiresAt = parseInt(expiresMatch[1], 10) * 1000;
      if (Date.now() > expiresAt) return true;
    }
    // Even non-expired Meshy URLs should be treated as temporary
    return true;
  }
  return false;
}

interface FixedRetargetedAvatarProps {
  avatarUrl: string;
  /** Base-colour override from an avatar "look". Null wears the original. */
  lookTextureUrl?: string | null;
  position: [number, number, number];
  disableCameraControl?: boolean;
  cameraDistanceOverride?: number;
  onGroupRef?: (ref: THREE.Group | null) => void;
  userName?: string;
  showNameTag?: boolean;
  // First-person mode: camera at eye level inside avatar, pointer-lock mouse look.
  // Movement direction is camera-yaw-relative. Avatar always faces where camera looks.
  firstPerson?: boolean;
  onLockChange?: (locked: boolean) => void;
  // Remote avatar props — when isRemote, position/rotation/animation are network-driven
  isRemote?: boolean;
  // Remote player's OWN Meshy animation URLs (broadcast over the network). A
  // remote Meshy avatar must animate with its own rig-specific clips; the shared
  // Mixamo fallbacks don't bind to Meshy skeletons and leave it in a T-pose.
  remoteAnimUrls?: Record<string, string> | null;
  targetPosition?: [number, number, number];
  targetRotation?: number;
  remoteAnimationState?: 'idle' | 'walk' | 'run';
  // Mutable ref for high-frequency network updates (avoids re-renders).
  // `lod` is assigned centrally by MultiplayerSync (distance/rank based):
  //   'full'      — animate + arm correction (nearest few players)
  //   'frozen'    — hold last pose, skip mixer + arm correction (CPU save)
  //   'billboard' — hide the skinned mesh, show a cheap capsule (GPU save)
  transformRef?: React.MutableRefObject<{ position: [number, number, number]; rotation: number; animationState: 'idle' | 'walk' | 'run'; emote?: { name: string; nonce: number } | null; lod?: 'full' | 'frozen' | 'billboard' }>;
  // Local avatar replacing another mid-session (avatar switch): start where the
  // previous body stood instead of at the spawn point (see avatar/carryPose).
  carryPose?: boolean;
  // Remote player's user id: VRM lip sync reads their LiveKit voice level.
  voiceUserId?: string | null;
}

// Shapes, models, lights and screens the editor can select (not the venue itself).
function isEditorObject(o: THREE.Object3D | null): boolean {
  const st = useWorldStore.getState();
  for (; o; o = o.parent) {
    const id = o.userData?.objectId as string | undefined;
    if (id && (st.primitives[id] || st.contentItems[id] || st.lights[id] || st.screenProps[id])) return true;
    if (o.name?.startsWith('primitive-') && st.primitives[o.name.slice(10)]) return true;
  }
  return false;
}

const FixedRetargetedAvatar: React.FC<FixedRetargetedAvatarProps> = (props) => {
  // If the avatar URL is dead/unreachable, render a capsule fallback instead of crashing
  if (isDeadAvatarUrl(props.avatarUrl)) {
    // A remote peer whose avatar can't be fetched reads as a ghost, same as a
    // view-only visitor, and keeps following their network transform.
    if (props.isRemote && props.transformRef) {
      return <GhostAvatar transformRef={props.transformRef} name={props.showNameTag !== false ? props.userName : undefined} />;
    }
    return (
      <group position={props.position}>
        <mesh position={[0, 1, 0]}>
          <capsuleGeometry args={[0.3, 1, 8, 16]} />
          <meshStandardMaterial color="#556677" />
        </mesh>
        {props.userName && props.showNameTag !== false && (
          <FloatingNameTagWithEvents name={props.userName} position={[0, 0, 0]} />
        )}
      </group>
    );
  }
  // VRM and glTF avatars load through different hooks: remount on a switch between them.
  return <FixedRetargetedAvatarInner key={isVrmUrl(props.avatarUrl) ? 'vrm' : 'gltf'} {...props} />;
};

// Post-animation arm correction (radians). Meshy library clips leave
// auto-rigged arms ~20-25° too wide (flared away from the body). Each frame
// both upper arms are rolled toward the body about the character's forward
// axis — computed in world space so left/right stay perfectly mirrored
// regardless of the rig's local bone axes. Live-tunable via
// window.__armRelax = <radians> for calibrating future rigs. These defaults
// were calibrated via the (now-removed) Avatar Pose sliders — they are the
// single source of truth so every browser renders the same pose (previously
// the good values lived only in each browser's localStorage, so browsers that
// had never been calibrated, e.g. Safari, showed arms flared/rolled back).
const ARM_RELAX_ANGLE = -0.35;
const REST_ARM_DROP_ANGLE = 1.25;
// Idle_9 also holds the shoulders shrugged up and pulled back; ease them down
// (roll, mirrored) and forward (pitch, same sign) while idling only — the
// walk/run clips carry natural shoulders. window.__shoulderRelax = {down, forward}.
const SHOULDER_RELAX = { down: 0.66, forward: -0.49 };
// Meshy's idle clips (Idle_6 on current rigs) are authored head-down: the clip
// drives `neck` to ~+26 deg of forward pitch and Spine02 to ~+13 deg for the
// whole 7.4s loop, so the avatar stands there studying the floor with a slumped
// nape. The same clip twists both wrists ~30 deg outward, which reads as the
// hands jutting forward off straight arms. The walk/run clips do NOT do this
// (neck ~-2 deg), so this correction — like SHOULDER_RELAX — is idle-only and
// blended. Live-tunable via window.__posture = {neck, chest, wrist, elbow}.
const POSTURE_RELAX = { hips: 0.22, neck: -0.24, chest: 0, wrist: -0.70, elbow: 0 };
const _relaxAxis = new THREE.Vector3();
const _relaxQ = new THREE.Quaternion();
const _relaxParentQ = new THREE.Quaternion();
const _relaxParentInv = new THREE.Quaternion();
// Rotate `bone` by `angle` about a character-space axis (converted to world):
// L' = P⁻¹ · R · P · L  where P is the parent's world rotation.
function rotateAboutCharacterAxis(bone: THREE.Object3D, ax: number, ay: number, az: number, angle: number, characterQuat: THREE.Quaternion) {
  if (!bone.parent || angle === 0) return;
  _relaxAxis.set(ax, ay, az).applyQuaternion(characterQuat);
  _relaxQ.setFromAxisAngle(_relaxAxis, angle);
  bone.parent.getWorldQuaternion(_relaxParentQ);
  _relaxParentInv.copy(_relaxParentQ).invert();
  bone.quaternion.premultiply(_relaxParentInv.multiply(_relaxQ).multiply(_relaxParentQ));
}
// Forward-axis roll toward the body (arms)
function applyArmAdduction(bone: THREE.Object3D, angle: number, characterQuat: THREE.Quaternion) {
  rotateAboutCharacterAxis(bone, 0, 0, 1, angle, characterQuat);
}

// Emote GLBs and the Tripo library skeleton are shared by every avatar on the
// page (remote players included), so each file is fetched once.
const emoteGltfCache = new Map<string, Promise<{ animations: THREE.AnimationClip[]; scene: THREE.Object3D }>>();
function loadEmoteGltf(url: string) {
  let p = emoteGltfCache.get(url);
  if (!p) {
    const loader = new GLTFLoader();
    try { loader.setDRACOLoader(getDRACOLoader()); } catch { /* draco optional */ }
    p = loader.loadAsync(url);
    p.catch(() => emoteGltfCache.delete(url));
    emoteGltfCache.set(url, p);
  }
  // Callers rename/filter the clip, so hand out the shared result read-only.
  return p;
}
/** The library avatar in bind pose: the rest skeleton every library clip was made on. */
function loadLibrarySource(): Promise<THREE.Object3D> {
  return loadEmoteGltf(LIBRARY_SOURCE_MODEL).then((g) => g.scene);
}

const FixedRetargetedAvatarInner: React.FC<FixedRetargetedAvatarProps> = ({ avatarUrl, lookTextureUrl = null, position, disableCameraControl = false, cameraDistanceOverride, onGroupRef, onLockChange, userName, showNameTag = true, firstPerson = false, isRemote = false, remoteAnimUrls = null, targetPosition, targetRotation, remoteAnimationState, transformRef, carryPose = false, voiceUserId = null }) => {
  const group = useRef<THREE.Group>(null);
  const billboardRef = useRef<THREE.Mesh>(null);
  const { camera, gl, scene: threeScene } = useThree();

  // Load avatar model
  // glTF/GLB via drei's cache, or a per-instance VRM (avatar.vrm set).
  const avatar = useAvatarModel(avatarUrl);
  // Local avatars read the worn look from the store, the same way animation
  // clips already do here. Remote avatars use whatever their owner broadcast;
  // until looks are added to the presence payload they simply wear the original,
  // which is correct-looking rather than wrong-looking.
  const storeLookTexture = useWorldStore((s) => (isRemote ? null : s.avatarLookTextureUrl));

  const storeAvatarAnimations = useWorldStore((s) => (isRemote ? null : s.avatarAnimations));

  // Animation URLs are rig-specific. For a REMOTE avatar we use the clips that
  // player broadcast for their OWN rig (remoteAnimUrls) — the shared Mixamo
  // fallbacks don't bind to a Meshy skeleton (mixamorig:* vs Hips/Spine01) and
  // leave the avatar in a T-pose. For the LOCAL avatar we read our own clips
  // from localStorage + the store.
  const meshyAnimUrls = useMemo(() => {
    if (isRemote) {
      return remoteAnimUrls && Object.keys(remoteAnimUrls).length > 0 ? remoteAnimUrls : null;
    }
    let storedAnims: Record<string, any> = {};
    try {
      const stored = localStorage.getItem('eb-avatar-animations');
      storedAnims = stored ? JSON.parse(stored) as Record<string, any> : {};
    } catch { /* keep empty */ }
    const merged = { ...storedAnims, ...(storeAvatarAnimations || {}) };
    return Object.keys(merged).length > 0 ? merged as Record<string, string> : null;
  }, [isRemote, remoteAnimUrls, storeAvatarAnimations]);

  // Size + hover hints ride along in the same JSON (see avatar/avatarMeta.ts),
  // so a remote viewer gets exactly what the owner sees.
  const meta = useMemo(() => readAvatarMeta(meshyAnimUrls), [meshyAnimUrls]);
  const targetHeight = meta.height;
  const hover = meta.hover;

  // Load animation GLBs. Generated Meshy avatars should only animate with
  // clips generated for their own rig; shared Mixamo fallbacks twist many
  // generated skeletons into sky-facing, crossed-leg poses.
  const idleModel = useGLTF(meshyAnimUrls?.idle || '/animations/idle-male.glb');
  const walkModel = useGLTF(meshyAnimUrls?.walking || '/animations/walk-male.glb');
  const runModel = useGLTF(meshyAnimUrls?.running || '/animations/run-male.glb');
  // Jump anim — load Meshy jump GLB or a dummy (hooks must be unconditional)
  const jumpModel = useGLTF(meshyAnimUrls?.jumping || '/animations/idle-male.glb');
  // Avatars built in-app on Tripo (functions/api/tripo/avatar.ts) have no clips
  // of their own: idle/walk/run/jump point at the shared Tripo library, which
  // was made on the library avatar's rig and must be retargeted onto this one
  // (Tripo rigs keep the picture's pose — T-pose library, often A-pose builds —
  // and differ in bone roll by up to ~160° at the feet). 7 KB.
  const librarySkeleton = useGLTF(LIBRARY_SOURCE_MODEL);
  const baseClipCache = useRef(new Map<string, THREE.AnimationClip>());
  /** This avatar's copy of a base clip: retargeted for library clips, else as authored. */
  const baseClip = (src: THREE.AnimationClip, url: string | undefined, name: string): THREE.AnimationClip => {
    if (!isTripoLibraryClip(url)) { const c = src.clone(); c.name = name; return c; }
    const key = `${avatarUrl}|${url}|${name}`;
    let c = baseClipCache.current.get(key);
    if (!c) {
      c = fitLibraryClip(src, librarySkeleton.scene, avatarRest);
      c.name = name;
      baseClipCache.current.set(key, c);
    }
    return c;
  };

  const hasMeshyAnims = !!(meshyAnimUrls?.walking || meshyAnimUrls?.running);
  const hasMeshyIdle = !!meshyAnimUrls?.idle;
  const hasMeshyJump = !!meshyAnimUrls?.jumping;

  // This avatar's rest pose (world quats + bone order), captured from the
  // pristine useGLTF scene — the mixer animates a clone, so `avatar.scene`
  // stays in bind pose. Used to retarget shared emote clips onto this rig.
  const avatarRest = useMemo(() => captureRest(avatar.scene), [avatar.scene]);
  // VRMs ground from a few joints (bind-pose clearances) instead of per-frame
  // skinned bounds over their ~90 primitives (see avatar/vrm/vrmGround.ts).
  const vrmGround = useMemo(() => (avatar.vrm ? createVrmGroundProbe(avatar.vrm) : null), [avatar.vrm]);

  // Tripo-rigged avatars play Tripo's own clips, which are already relaxed and
  // upright — the Meshy arm/shoulder/posture corrections below would bend them.
  // VRMs play the same Tripo library clips (retargeted by body part), so they
  // take the Tripo path too: no Meshy arm/posture corrections.
  const tripoRig = useMemo(() => isTripoRig(avatar.scene) || !!avatar.vrm, [avatar.scene, avatar.vrm]);

  // ---- Emote clips ----
  // Loaded imperatively (not via useGLTF) so a missing/404 emote GLB can't throw
  // in Suspense and crash the avatar. Each clip is renamed to its emote name,
  // made to fit THIS rig (prepareEmote) and merged into `animations`, so
  // useAnimations exposes actions[emoteName]. Emotes that fail to load, or
  // carry no animation track, are simply skipped. Tripo library moves outside
  // the wheel load on first play instead (see requestEmote).
  const [emoteClips, setEmoteClips] = useState<THREE.AnimationClip[]>([]);
  const prepareEmote = (name: string): Promise<THREE.AnimationClip | null> => {
    const def = EMOTE_MAP[name];
    if (!def) return Promise.resolve(null);
    // Prefer a per-avatar emote clip generated on THIS avatar's own rig (key
    // `emote_<name>`), then — on a Tripo rig — the shared Tripo library move,
    // then the shared Meshy-era clip.
    const ownUrl = meshyAnimUrls?.[`emote_${name}`];
    const lib = def.category ? def : tripoRig && TRIPO_EQUIVALENT[name] ? EMOTE_MAP[TRIPO_EQUIVALENT[name]] : undefined;
    const url = ownUrl || lib?.url || def.url;
    return loadEmoteGltf(url)
      .then(async (gltf) => {
        if (!gltf.animations?.length) return null;
        const clip = gltf.animations[0].clone();
        clip.name = name;
        // Tripo's per-avatar emotes are generated on this exact skeleton: play
        // their rotations directly, like the base clips. Their files don't keep
        // the skeleton at rest, so world-delta retargeting would bend them.
        if (ownUrl && tripoRig) {
          clip.tracks = clip.tracks.filter((t) => t.name.endsWith('.quaternion'));
          return clip;
        }
        // Library moves: same reason, so retarget from the library avatar's
        // skeleton (pristine copy — retargeting poses it) rather than the file's.
        // Always a copy: the GLB is cached and shared, and retargeting poses it.
        let src = clone(gltf.scene as THREE.Object3D);
        if (!ownUrl && lib) {
          src = clone(await loadLibrarySource());
          clip.tracks = clip.tracks.filter((t) => t.name.endsWith('.quaternion'));
        }
        try {
          return retargetClipToRig(src, clip, avatarRest.restWorldQ, avatarRest.boneOrder, avatarRest.restWorldPos);
        } catch (err) {
          console.warn(`[Emote] Retarget failed for "${name}" — using raw clip`, err);
          return clip;
        }
      })
      .catch(() => {
        console.warn(`[Emote] Could not load "${name}" (${url}) — skipping`);
        return null;
      });
  };
  useEffect(() => {
    let cancelled = false;
    Promise.all(EMOTES.map((e) => prepareEmote(e.name))).then((clips) => {
      if (cancelled) return;
      const valid = clips.filter((c): c is THREE.AnimationClip => !!c);
      if (valid.length > 0) {
        console.log('[Emote] Loaded clips:', valid.map((c) => c.name).join(', '));
        setEmoteClips(valid);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meshyAnimUrls, avatarRest, tripoRig]);

  // Name of the emote currently overriding the movement animations (null = none)
  const activeEmote = useRef<string | null>(null);
  // Last emote nonce applied on the remote path (dedupes repeated frames)
  const lastRemoteEmote = useRef<{ name: string; nonce: number } | null>(null);
  // Local player's emote trigger (remote avatars never subscribe → null)
  const localEmote = useWorldStore((s) => (isRemote ? null : s.localEmote));

  // Clone avatar and normalize position
  const avatarWrapper = useMemo(() => {
    // A VRM instance belongs to this avatar alone (can't be cloned): use it
    // directly, resetting the root first in case this memo re-runs.
    // Tripo builds with arm weights on the head tear the hair/face as soon as
    // the arms move (see avatar/skinRepair.ts). Shared geometry: once per model.
    if (!avatar.vrm) {
      const n = repairHeadArmWeights(avatar.scene);
      if (n) console.log(`[Avatar] Repaired ${n} head vertices weighted to the arms`);
    }
    const cloned = avatar.vrm ? avatar.scene : clone(avatar.scene);
    if (avatar.vrm) { cloned.scale.setScalar(1); cloned.position.set(0, 0, 0); }

    // Normalize from transformed object bounds instead of raw vertex Y values.
    // Skinned avatars often keep geometry in bind-space under transformed
    // parents; raw vertex scans can place the visible body below the floor.
    cloned.updateMatrixWorld(true);
    let box = new THREE.Box3().setFromObject(cloned);
    // Avatars with a declared standing height (__height) are scaled to it.
    // Clips are rotation-only, so scale never affects animation.
    const h = box.max.y - box.min.y;
    if (targetHeight && Number.isFinite(h) && h > 0.01 && Math.abs(targetHeight / h - 1) > 0.01) {
      cloned.scale.multiplyScalar(targetHeight / h);
      cloned.updateMatrixWorld(true);
      box = new THREE.Box3().setFromObject(cloned);
    }
    if (Number.isFinite(box.min.y)) {
      cloned.position.y -= box.min.y;
    }

    const wrapper = new THREE.Group();
    wrapper.add(cloned);

    return wrapper;
  }, [avatar.scene, targetHeight]);

  useLookTexture(avatarWrapper, lookTextureUrl ?? storeLookTexture);
  // Deep-seat sit (see the chair-sit pass after the main frame).
  const chairFit = useMemo(() => new ChairSitFitter(avatarWrapper), [avatarWrapper]);
  const chairSeat = useRef<{ x: number; z: number; seat: ChairSeat; floor: number | null; at: number } | null>(null);
  // Remote avatars: the height they last stood at, the floor under their seat.
  const standY = useRef<number | null>(null);
  const sitSettledAt = useRef<number | null>(null);

  // Head bone of a local glTF avatar, hidden in first person (useFrame).
  const fpHead = useMemo(() => {
    if (isRemote || avatar.vrm) return null;
    let bone: THREE.Bone | null = null;
    let loose: THREE.Bone | null = null;
    avatarWrapper.traverse((o) => {
      if (bone || !(o as THREE.Bone).isBone) return;
      const n = o.name.toLowerCase().replace(/^mixamorig:?/, '');
      if (n === 'head' || n === 'head_m') bone = o as THREE.Bone;
      else if (!loose && n.includes('head') && !/top|end|tip|nub/.test(n)) loose = o as THREE.Bone;
    });
    const b = (bone ?? loose) as THREE.Bone | null;
    return b ? { bone: b, scale: b.scale.clone(), hidden: false } : null;
  }, [avatarWrapper, avatar.vrm, isRemote]);

  // Note: Meshy Animation Library animations are generated for the same rig,
  // so rest poses are identical — no retargeting needed.

  // Build animation clips — Meshy animations are natively compatible, no retargeting needed
  const animations = useMemo(() => {
    const anims: THREE.AnimationClip[] = [];

    // Idle: use Meshy idle animation if available, else the avatar GLB's own
    // embedded animation, else slowed same-rig walk. Do not use shared fallback
    // clips on generated rigs.
    if (hasMeshyIdle && idleModel.animations.length > 0) {
      const c = baseClip(idleModel.animations[0], meshyAnimUrls?.idle, 'idle');
      anims.push(c);
      console.log(`[Avatar] Using Meshy idle animation (${idleModel.animations[0].duration.toFixed(2)}s)`);
    } else if (avatar.animations.length > 0) {
      const c = avatar.animations[0].clone();
      c.name = 'idle';
      anims.push(c);
      console.log(`[Avatar] Using embedded avatar idle animation (${avatar.animations[0].duration.toFixed(2)}s)`);
    } else if (hasMeshyAnims && walkModel.animations.length > 0) {
      const c = baseClip(walkModel.animations[0], meshyAnimUrls?.walking, 'idle');
      anims.push(c);
      console.log('[Avatar] Using slowed walk as idle (no Meshy idle anim)');
    }

    if (meshyAnimUrls?.walking && walkModel.animations.length > 0) {
      const c = baseClip(walkModel.animations[0], meshyAnimUrls?.walking, 'walk');
      anims.push(c);
      console.log(`[Avatar] Walk animation: "${walkModel.animations[0].name}" (${walkModel.animations[0].duration.toFixed(2)}s) source=meshy`);
    }

    if (meshyAnimUrls?.running && runModel.animations.length > 0) {
      const c = baseClip(runModel.animations[0], meshyAnimUrls?.running, 'run');
      anims.push(c);
      console.log(`[Avatar] Run animation: "${runModel.animations[0].name}" (${runModel.animations[0].duration.toFixed(2)}s) source=meshy`);
    }

    if (hasMeshyJump && jumpModel.animations.length > 0) {
      const c = baseClip(jumpModel.animations[0], meshyAnimUrls?.jumping, 'jump');
      anims.push(c);
      console.log(`[Avatar] Jump animation: "${jumpModel.animations[0].name}" (${jumpModel.animations[0].duration.toFixed(2)}s)`);
    }

    // Merge emote clips (already renamed and fitted to this rig by
    // prepareEmote). Skip any whose name collides with a base clip just in case.
    for (const clip of emoteClips) {
      if (!anims.some((a) => a.name === clip.name)) anims.push(clip);
    }

    console.log('[Avatar] Animations loaded:', anims.map(a => `${a.name}(${a.duration.toFixed(1)}s)`).join(', ') || 'none');
    return anims;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarWrapper, emoteClips, avatarRest]);

  // Set up animations on the avatar wrapper (contains the cloned scene)
  const { actions, mixer } = useAvatarAnimator(animations, avatarWrapper);
  
  // Animation state
  const currentAnimation = useRef('idle');
  // Accumulated delta for the 10Hz remote-mixer throttle during VR build mode
  const mixerThrottleAccum = useRef(0);
  // Clock time of the last skinned-bounds grounding pass (remote, in XR).
  const lastGroundAt = useRef(-Infinity);

  // Movement state
  const velocity = useRef(new THREE.Vector3());
  const direction = useRef(new THREE.Vector3());
  const raycaster = useRef(new THREE.Raycaster());
  const keys = useRef({
    w: false,
    a: false,
    s: false,
    d: false,
    shift: false,
    ' ': false,
  });
  const jumpVelocity = useRef(0);
  const isJumping = useRef(false);
  const jumpBlend = useRef(0); // 0 = no jump pose, 1 = full jump pose
  const shoulderBlend = useRef(0); // 0 = clip shoulders, 1 = full idle shoulder relax
  // Hover bots: the lift currently added to avatarWrapper (removed before the
  // ground keeper runs, so it never accumulates), a per-avatar bob phase so a
  // room of bots doesn't bob in unison, and the walk leg-damping blend.
  const hoverApplied = useRef(0);
  const hoverPhase = useRef(Math.random() * Math.PI * 2);
  const legDampBlend = useRef(0);
  const _legRest = useMemo(() => new THREE.Quaternion(), []);

  // Cached bone refs for procedural jump animation
  const jumpBones = useRef<{
    leftArm?: THREE.Bone;
    rightArm?: THREE.Bone;
    leftShoulder?: THREE.Bone;
    rightShoulder?: THREE.Bone;
    leftForeArm?: THREE.Bone;
    rightForeArm?: THREE.Bone;
    leftHand?: THREE.Bone;
    rightHand?: THREE.Bone;
    hips?: THREE.Bone;
    neck?: THREE.Bone;
    chest?: THREE.Bone;
    leftUpLeg?: THREE.Bone;
    rightUpLeg?: THREE.Bone;
    leftLeg?: THREE.Bone;
    rightLeg?: THREE.Bone;
    spine?: THREE.Bone;
  }>({});
  const bonesFound = useRef(false);
  const restBoneQuats = useRef<Record<string, THREE.Quaternion>>({});
  const _groundBox = useMemo(() => new THREE.Box3(), []);

  // Click-to-move state
  const clickTarget = useRef<THREE.Vector3 | null>(null);
  const clickMarkerRef = useRef<THREE.Mesh>(null);
  const clickMarkerOpacity = useRef(0);
  // Pending "sit on this seat" request — set when a marked seat is clicked;
  // the avatar walks to it (clickTarget) and, once settled, snaps onto the seat
  // facing the fire and plays the Sit emote.
  // A pending sit: the seat's centre, facing, top surface and the height
  // window the avatar may be placed in. The sitting style (chair / floor /
  // squat) is applied at snap time, so it decides the final height and perch.
  // tableTop: world height of the game table a chair faces (first person keeps the eye above it).
  type SeatInfo = { x: number; z: number; yaw: number; surface: number; minY: number; maxY: number; perch: number; hipH?: number; floor?: number; fit?: 'chair'; tableTop?: number };
  const sitTarget = useRef<(SeatInfo & { t: number }) | null>(null);
  // The seat the avatar is on right now (null when standing). Lets the player
  // switch sitting style in place.
  const seatedOn = useRef<SeatInfo | null>(null);
  // Place the avatar on a seat in a given sitting style. Chair sits perch
  // forward so the legs hang off the front edge; floor/squat sit centred.
  const placeOnSeat = (seat: SeatInfo, emote: string) => {
    if (!group.current) return;
    const style = sitStyleFor(emote) ?? getSitStyle();
    const fitted = style.emote === 'sit' && seat.fit === 'chair' && !!seat.hipH;
    const perch = fitted
      ? CHAIR_HIP_AHEAD - CHAIR_POSE_HIP_FWD * seat.hipH!
      : style.emote === 'sit' ? seat.perch : FLOOR_PERCH;
    group.current.position.x = seat.x + Math.sin(seat.yaw) * perch;
    group.current.position.z = seat.z + Math.cos(seat.yaw) * perch;
    // Floor/squat: put the hips just above the seat surface. In these poses
    // the hips sit at poseHip × the avatar's standing hip height above the
    // root, so lower the root by that much.
    let y = style.poseHip != null && seat.hipH
      ? seat.surface + SEAT_CLEARANCE - style.poseHip * seat.hipH
      : seat.surface - style.drop;
    // Fitted chair: scale to the avatar's hip height (see CHAIR_POSE_HIP),
    // bounded so the feet don't sink into the floor the avatar walked up on.
    if (fitted && seat.hipH) {
      y = seat.surface + CHAIR_HIP_CLEARANCE - CHAIR_POSE_HIP * seat.hipH;
      if (seat.floor != null) y = Math.max(y, seat.floor - CHAIR_POSE_FOOT * seat.hipH - CHAIR_FEET_SINK);
    }
    // Floor styles and fitted chairs may go below the standing height (the
    // root sinks so the hips sit on the cushion); never more than one
    // hip-height down.
    const minY = seat.hipH && (style.poseHip != null || fitted) ? seat.minY - seat.hipH : seat.minY;
    group.current.position.y = Math.min(Math.max(y, minY), seat.maxY);
    targetYaw.current = seat.yaw;
  };
  const pointerDownTime = useRef(0);
  const pointerDownPos = useRef({ x: 0, y: 0 });
  
  // Camera controls (third-person orbit)
  const cameraDistance = useRef(cameraDistanceOverride ?? 10);
  const cameraAngle = useRef({ x: 0, y: 0 });
  const isMouseDown = useRef(false);

  // First-person camera state
  const fpYaw = useRef(0);     // horizontal look angle (matches camera.rotation.y)
  const fpPitch = useRef(0);   // vertical look angle (camera.rotation.x, clamped ±1.4)
  const fpLocked = useRef(false); // true while pointer lock is active
  // Ref so closures inside the keyboard/mouse useEffect can read the current prop value
  const firstPersonRef = useRef(firstPerson);
  useEffect(() => { firstPersonRef.current = firstPerson; }, [firstPerson]);
  // Camera yaw convention (fpYaw and cameraAngle.x alike): the view looks along
  // (-sin yaw, 0, -cos yaw). The model faces local +Z, so a body facing the
  // view has rotation.y = yaw + PI. Switching modes hands the facing across
  // so neither the body nor the view direction snaps.
  const wasFirstPerson = useRef(firstPerson);
  useEffect(() => {
    if (isRemote || wasFirstPerson.current === firstPerson) return;
    wasFirstPerson.current = firstPerson;
    if (firstPerson) {
      // Look where the body already faces, level.
      fpYaw.current = (group.current?.rotation.y ?? targetYaw.current) - Math.PI;
      fpPitch.current = 0;
    } else {
      // Orbit camera behind the body, looking the way first-person looked.
      cameraAngle.current.x = fpYaw.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstPerson, isRemote]);

  // Apply camera distance override when it changes (e.g., zoom mode). When the
  // override clears (pressing Normal after Zoom), snap back to the default
  // distance — otherwise the camera stays stuck at the zoomed-in distance.
  useEffect(() => {
    cameraDistance.current = cameraDistanceOverride ?? 10;
  }, [cameraDistanceOverride]);

  // Pre-allocated vectors (avoid GC pressure)
  const _lerpTarget = useMemo(() => new THREE.Vector3(), []);
  const _colBox = useMemo(() => new THREE.Box3(), []);
  const _seatProbe = useMemo(() => new THREE.Vector3(), []);
  const _forward = useMemo(() => new THREE.Vector3(), []);
  const _right = useMemo(() => new THREE.Vector3(), []);
  const _desiredVel = useMemo(() => new THREE.Vector3(), []);
  const _camOffset = useMemo(() => new THREE.Vector3(), []);
  const _camDesired = useMemo(() => new THREE.Vector3(), []);
  const _lookTarget = useMemo(() => new THREE.Vector3(), []);
  const _camRay = useMemo(() => new THREE.Ray(), []);
  // Walking to a seat: closest distance reached so far and when it last
  // improved, so a walk that jams against furniture can still finish.
  const seatApproach = useRef({ best: Infinity, t: 0 });
  const _camCaster = useMemo(() => new THREE.Raycaster(), []);
  const _camClamp = useMemo<CameraClamp>(() => ({ floorY: -Infinity, ceilingY: Infinity }), []);
  // Smoothed facing angle for third-person (avoids instant snap on turns)
  const targetYaw = useRef(0);
  const yawInit = useRef(false);

  // Cache collidable meshes (refresh periodically, not every frame)
  // Mesh + its world AABB. Boxes are computed when the cache is rebuilt, not
  // per frame: an uploaded venue contributes hundreds of collidable meshes and
  // setFromObject on each of them every frame is what turns collision from a
  // feature into a frame-rate problem. Environment geometry is static, so a
  // box is still valid until the next rebuild.
  const collidableCache = useRef<{ mesh: THREE.Mesh; box: THREE.Box3 }[]>([]);
  // Wide, thin, NON-collidable box/plane meshes (ceilings, upper floors): the
  // orbit camera treats the ones overhead as blockers so it can't rise
  // through an unflagged ceiling (utils/cameraArm). Rebuilt with the cache.
  const slabCache = useRef<THREE.Box3[]>([]);
  const lastCacheTime = useRef(0);

  // Initialize animations
  useEffect(() => {
    if (actions.idle && mixer) {
      console.log('Initializing animations');
      console.log('Available actions:', Object.keys(actions));

      // Stop all current actions
      Object.values(actions).forEach(action => {
        if (action) action.stop();
      });

      // Play idle — if using walk-as-idle fallback, slow it down.
      // Meshy idles play slightly slowed: full speed reads as heavy breathing.
      actions.idle.reset().play();
      if (hasMeshyAnims && !hasMeshyIdle) {
        actions.idle.timeScale = 0.25; // Slow walk = natural idle sway
      } else {
        actions.idle.timeScale = (window as any).__idleSpeed ?? 0.65;
      }
      currentAnimation.current = 'idle';
    }
  }, [actions, mixer, hasMeshyAnims, hasMeshyIdle]);

  // ---- Emote playback ----
  // Start an emote: crossfade out whatever is playing and play the emote clip.
  // loop:false plays once (clamped, cleared on the mixer 'finished' event);
  // loop:true loops until cancelled by movement. Guards missing actions so a
  // not-yet-generated emote is a no-op.
  const wantedEmote = useRef<string | null>(null);
  const playEmote = (name: string) => {
    const def = EMOTE_MAP[name];
    const action = actions[name];
    wantedEmote.current = name;
    // Library moves load on first play, then join this avatar's mixer.
    if (def?.category && !action) {
      prepareEmote(name).then((clip) => {
        if (!clip || wantedEmote.current !== name) return;
        if (!actions[name]) actions[name] = mixer.clipAction(clip);
        playEmote(name);
      });
      return;
    }
    if (!def || !action) {
      console.warn(`[Emote] "${name}" is not available yet — ignoring`);
      return;
    }
    const prevName = activeEmote.current ?? currentAnimation.current;
    const prev = actions[prevName];
    if (prev && prev !== action) prev.fadeOut(0.15);
    action.reset().fadeIn(0.15).play();
    if (def.loop) {
      action.setLoop(THREE.LoopRepeat, Infinity);
      action.clampWhenFinished = false;
    } else {
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;
    }
    activeEmote.current = name;
  };

  // One-shot emotes clear themselves and fade back to the movement animation
  // when the clip finishes. (Jump also uses LoopOnce, but activeEmote is null
  // then, so the guard skips it.)
  useEffect(() => {
    if (!mixer) return;
    const onFinished = (e: any) => {
      if (!activeEmote.current) return;
      if (actions[activeEmote.current] !== e.action) return;
      activeEmote.current = null; wantedEmote.current = null;
      if (!isRemote) useWorldStore.getState().stopLocalEmote();
      const resume = actions[currentAnimation.current];
      if (resume) resume.reset().fadeIn(0.2).play();
    };
    mixer.addEventListener('finished', onFinished);
    return () => mixer.removeEventListener('finished', onFinished);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mixer, actions, isRemote]);

  // LOCAL: fire on each localEmote trigger (object identity changes per nonce,
  // so the same emote re-triggers).
  useEffect(() => {
    if (isRemote) return;
    if (!localEmote) {
      wantedEmote.current = null;
      if (activeEmote.current) {
        actions[activeEmote.current]?.fadeOut(.15);
        activeEmote.current = null; wantedEmote.current = null;
        actions[currentAnimation.current]?.reset().fadeIn(.2).play();
      }
      return;
    }
    // Switching sitting style while on a seat: re-seat at that style's height
    // and perch on the same seat.
    if (seatedOn.current && SIT_EMOTES.has(localEmote.name)) {
      placeOnSeat(seatedOn.current, localEmote.name);
    }
    playEmote(localEmote.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localEmote]);

  // Find and cache bones for jump animation
  useEffect(() => {
    if (bonesFound.current) return;
    // Support both Mixamo (Spine1) and Meshy (Spine01) bone naming
    const boneMap: Record<string, string> = {
      'LeftArm': 'leftArm', 'RightArm': 'rightArm',
      'LeftForeArm': 'leftForeArm', 'RightForeArm': 'rightForeArm',
      'LeftShoulder': 'leftShoulder', 'RightShoulder': 'rightShoulder',
      'LeftUpLeg': 'leftUpLeg', 'RightUpLeg': 'rightUpLeg',
      'LeftLeg': 'leftLeg', 'RightLeg': 'rightLeg',
      'Spine1': 'spine', 'Spine01': 'spine',
      'LeftHand': 'leftHand', 'RightHand': 'rightHand',
      'neck': 'neck', 'Neck': 'neck', 'Hips': 'hips',
      'Spine02': 'chest', 'Spine2': 'chest',
      // Tripo skeleton
      'L_Upperarm': 'leftArm', 'R_Upperarm': 'rightArm',
      'L_Forearm': 'leftForeArm', 'R_Forearm': 'rightForeArm',
      'L_Clavicle': 'leftShoulder', 'R_Clavicle': 'rightShoulder',
      'L_Thigh': 'leftUpLeg', 'R_Thigh': 'rightUpLeg',
      'L_Calf': 'leftLeg', 'R_Calf': 'rightLeg',
      'L_Hand': 'leftHand', 'R_Hand': 'rightHand',
      'NeckTwist01': 'neck', 'Hip': 'hips',
    };
    avatarWrapper.traverse((child: any) => {
      if (child.isBone && boneMap[child.name]) {
        (jumpBones.current as any)[boneMap[child.name]] = child;
        restBoneQuats.current[boneMap[child.name]] = child.quaternion.clone();
      }
    });
    if (jumpBones.current.leftArm) {
      bonesFound.current = true;
      console.log('[Jump] Found bones:', Object.keys(jumpBones.current).filter(k => (jumpBones.current as any)[k]));
    }
  }, [avatarWrapper]);

  const keepAvatarOnGround = () => {
    if (!group.current) return;
    if (vrmGround) {
      avatarWrapper.updateMatrixWorld(true);
      const off = vrmGround.lowestY() - group.current.position.y;
      if (Number.isFinite(off) && Math.abs(off) > 0.005) avatarWrapper.position.y -= off;
      return;
    }
    avatarWrapper.traverse((child: any) => {
      if (child.isSkinnedMesh) {
        child.skeleton?.update();
        child.computeBoundingBox();
      }
    });
    avatarWrapper.updateMatrixWorld(true);
    _groundBox.setFromObject(avatarWrapper);
    const offsetFromAvatarRoot = _groundBox.min.y - group.current.position.y;
    if (Number.isFinite(offsetFromAvatarRoot) && Math.abs(offsetFromAvatarRoot) > 0.005) {
      avatarWrapper.position.y -= offsetFromAvatarRoot;
    }
  };

  const applyStaticRestPose = () => {
    if (!group.current || !bonesFound.current || activeEmote.current || jumpBlend.current > 0) return;
    const b = jumpBones.current;
    const rest = restBoneQuats.current;

    for (const key of Object.keys(rest)) {
      const bone = (b as any)[key] as THREE.Bone | undefined;
      if (bone) bone.quaternion.copy(rest[key]);
    }

    const q = group.current.quaternion;
    const drop = (window as any).__restArmDrop ?? REST_ARM_DROP_ANGLE;
    if (b.leftArm) applyArmAdduction(b.leftArm, -drop, q);
    if (b.rightArm) applyArmAdduction(b.rightArm, drop, q);
  };

  // Initialize position on mount — and keep honoring spawn-point updates
  // until the player first moves. The world's saved spawnPosition often loads
  // AFTER the avatar mounts, so a strict once-only init would leave the avatar
  // at the default origin instead of the placed spawn point.
  const hasInitialized = useRef(false);
  const hasMoved = useRef(false);

  // Portal travel (local player only): step out of the exit, facing away
  // from it, with the camera swung round behind.
  usePortalArrival(({ position: p, yaw }) => {
    if (isRemote || !group.current) return;
    group.current.position.set(p[0], p[1], p[2]);
    group.current.rotation.y = yaw;
    targetYaw.current = yaw;
    cameraAngle.current.x = yaw + Math.PI;
    velocity.current.set(0, 0, 0);
    clickTarget.current = null;
    sitTarget.current = null;
    seatedOn.current = null;
    hasMoved.current = true;
    if (activeEmote.current) {
      activeEmote.current = null; wantedEmote.current = null;
      currentAnimation.current = '__none';
      useWorldStore.getState().stopLocalEmote();
    }
  });
  useEffect(() => {
    const carried = group.current && !hasInitialized.current && carryPose && !isRemote ? takeLocalPose(120_000) : null;
    if (group.current && carried) {
      // Avatar switch: take over from the previous body (no spawn, no camera reset).
      hasInitialized.current = true;
      hasMoved.current = true;
      group.current.position.set(...carried.position);
      group.current.rotation.y = carried.yaw;
      targetYaw.current = carried.yaw;
      cameraAngle.current = { ...carried.camera };
      fpYaw.current = carried.fpYaw;
      fpPitch.current = carried.fpPitch;
      // An emote belonged to the old rig; stop it here and for everyone else.
      const st = useWorldStore.getState();
      if (st.localEmote) {
        st.stopLocalEmote();
        const wid = st.actualWorldId || getCurrentWorldId();
        if (wid) broadcastEmote(wid, null);
      }
      // Was sitting: sit back down on the same seat, measured for this body.
      if (carried.seat) sitTarget.current = { ...carried.seat, t: performance.now() };
    } else if (group.current && !hasInitialized.current) {
      hasInitialized.current = true;
      group.current.position.set(...position);
      if (!isRemote) {
        camera.position.set(0, 5, 10);
        camera.lookAt(0, 1, 0);
      }
    } else if (group.current && !isRemote && !hasMoved.current) {
      // Spawn point arrived after mount (world settings load async).
      group.current.position.set(...position);
    }

    // Call the ref callback if provided
    if (group.current && onGroupRef) {
      onGroupRef(group.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, camera, onGroupRef, isRemote]);

  // Leave this body's pose behind when it unmounts, for a replacement avatar
  // (carryPose) to take over from. Seated: stand at the seat's floor level.
  useEffect(() => {
    if (isRemote) return;
    const g = group.current;
    return () => {
      if (!g) return;
      const seated = seatedOn.current;
      const seat = seated ? { x: seated.x, z: seated.z, yaw: seated.yaw, surface: seated.surface, minY: seated.minY, maxY: seated.maxY, perch: seated.perch, floor: seated.floor, fit: seated.fit } : null;
      leaveLocalPose({
        position: [g.position.x, seat?.floor ?? g.position.y, g.position.z],
        yaw: g.rotation.y,
        camera: { ...cameraAngle.current },
        fpYaw: fpYaw.current,
        fpPitch: fpPitch.current,
        seat,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRemote]);

  // First-person pointer-lock mouse look (local avatar, firstPerson mode only)
  useEffect(() => {
    if (!firstPerson || isRemote) return;

    const canvas = gl.domElement;

    const onPointerLockChange = () => {
      const locked = document.pointerLockElement === canvas;
      fpLocked.current = locked;
      onLockChange?.(locked);
    };
    const onFpMouseMove = (e: MouseEvent) => {
      if (!fpLocked.current) return;
      fpYaw.current -= e.movementX * 0.002;
      fpPitch.current = Math.max(-1.4, Math.min(1.4, fpPitch.current - e.movementY * 0.002));
    };
    const onCanvasClick = () => {
      if (!fpLocked.current) canvas.requestPointerLock?.();
    };

    canvas.addEventListener('click', onCanvasClick);
    document.addEventListener('pointerlockchange', onPointerLockChange);
    document.addEventListener('mousemove', onFpMouseMove);

    return () => {
      canvas.removeEventListener('click', onCanvasClick);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      document.removeEventListener('mousemove', onFpMouseMove);
      if (document.pointerLockElement === canvas) document.exitPointerLock();
    };
  }, [firstPerson, isRemote, gl]);

  // Keyboard and mouse controls (local avatars only)
  useEffect(() => {
    if (isRemote) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key === ' ' && !isJumping.current) {
        // Jumping cancels any active emote first
        if (activeEmote.current) {
          const a = actions[activeEmote.current];
          if (a) a.fadeOut(0.1);
          activeEmote.current = null; wantedEmote.current = null;
          useWorldStore.getState().stopLocalEmote();
        }
        isJumping.current = true;
        jumpVelocity.current = 8; // Jump impulse
        // Play jump animation clip if available
        if (hasMeshyJump && actions.jump) {
          const prevAction = actions[currentAnimation.current];
          if (prevAction) prevAction.fadeOut(0.1);
          actions.jump.reset().fadeIn(0.1).setLoop(THREE.LoopOnce, 1).play();
          actions.jump.clampWhenFinished = true;
        }
      }
      if (key in keys.current) {
        keys.current[key as keyof typeof keys.current] = true;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key in keys.current) {
        keys.current[key as keyof typeof keys.current] = false;
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Only start camera orbit when the press begins on the 3D canvas —
      // drags on UI overlays (sliders, panels) must not move the scene.
      if (!(e.target instanceof HTMLCanvasElement)) return;
      if (e.button === 0) { // Left click — camera orbit
        isMouseDown.current = true;
        pointerDownTime.current = performance.now();
        pointerDownPos.current = { x: e.clientX, y: e.clientY };
        e.preventDefault();
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      // isMouseDown is only set for presses that began on the canvas, so a
      // click/tap on a HUD button never walks the avatar.
      if (e.button === 0 && isMouseDown.current) {
        const elapsed = performance.now() - pointerDownTime.current;
        const dx = e.clientX - pointerDownPos.current.x;
        const dy = e.clientY - pointerDownPos.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        // Short click with no drag = click-to-move (disabled in first-person where
        // clicking requests pointer lock instead)
        if (elapsed < 300 && dist < 5 && !(window as any).__transformDragging && !firstPersonRef.current) {
          handleClickToMove(e.clientX, e.clientY);
        }
      }
      isMouseDown.current = false;
    };

    // Right-click also triggers click-to-move (useful when left-click is for orbiting)
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      handleClickToMove(e.clientX, e.clientY);
    };

    // Walk to a seat object, then sit on it. Used by seat clicks and by game
    // tables (taking a seat at a table seats the avatar in that chair).
    const sitOn = (seatObj: THREE.Object3D) => {
      if (!group.current) return;
      const wp = seatObj.getWorldPosition(new THREE.Vector3());
      clickTarget.current = new THREE.Vector3(wp.x, position[1], wp.z);
      // Face the seat's own forward (+Z in its local frame) — chairs point
      // where their builder aimed them. Seats with no rotation still work:
      // identity quaternion faces world +Z.
      const fwd = new THREE.Vector3(0, 0, 1).applyQuaternion(seatObj.getWorldQuaternion(new THREE.Quaternion()));
      // Sit height comes from the seat's top surface, not its origin, so
      // this works for both a chair on the floor and a raised theatre tier.
      // How far below it the root goes depends on the sitting style.
      const seatBox = new THREE.Box3().setFromObject(seatObj);
      const seatSize = seatBox.getSize(new THREE.Vector3());
      // Perch forward of centre so legs hang off the front instead of
      // clipping through — but scaled to the seat, or a small chair puts
      // the avatar on its front lip.
      const perch = Math.min(0.35, Math.max(0.08, Math.max(seatSize.x, seatSize.z) * 0.26));
      // Never fall back to where the avatar happens to be standing: an
      // empty box (these markers are zero-opacity meshes) would then seat
      // everyone at floor level, which only looks right on the front row.
      // The marker's own height is always the correct reference.
      const surfaceY = seatBox.isEmpty() ? wp.y + 0.25 : seatBox.max.y;
      seatApproach.current = { best: Infinity, t: performance.now() };
      sitTarget.current = {
        x: wp.x, z: wp.z, yaw: Math.atan2(fwd.x, fwd.z),
        surface: surfaceY,
        // never sink below where we're standing, and ignore absurd values
        // from an over-large object someone flagged as a seat
        minY: group.current.position.y,
        maxY: group.current.position.y + 6,
        perch,
        // 'deep' seats are fitted chairs whose legs are also posed (chair-sit pass).
        fit: seatObj.userData?.seatFit === 'chair' || seatObj.userData?.seatFit === 'deep' ? 'chair' : undefined,
        tableTop: seatTableTop(seatObj),
        t: performance.now(),
      };
      clickMarkerOpacity.current = 1;
      keys.current.w = keys.current.a = keys.current.s = keys.current.d = false;
    };
    const onGameSit = (e: Event) => {
      const seat = (e as CustomEvent).detail?.seat as THREE.Object3D | undefined;
      if (seat) sitOn(seat);
    };
    window.addEventListener('gathrxr:game-sit', onGameSit);
    // Leaving a game table stands the avatar up (if it is sitting).
    const onGameStand = () => {
      sitTarget.current = null;
      if (!activeEmote.current || !SIT_EMOTES.has(activeEmote.current)) return;
      activeEmote.current = null; wantedEmote.current = null;
      seatedOn.current = null;
      currentAnimation.current = '__none';
      useWorldStore.getState().stopLocalEmote();
      const wid = useWorldStore.getState().actualWorldId || getCurrentWorldId();
      if (wid) broadcastEmote(wid, null);
    };
    window.addEventListener('gathrxr:game-stand', onGameStand);

    const handleClickToMove = (clientX: number, clientY: number) => {
      if (!group.current) return;
      // Don't teleport when dragging TransformControls, or while a Build Mode v2
      // tool (place / wall / room / paint) owns the pointer.
      if ((window as any).__transformDragging) return;
      if ((window as any).__buildToolActive) return;
      const canvas = document.querySelector('canvas');
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1
      );
      raycaster.current.setFromCamera(mouse, camera);
      // Raycast against ground and collidable objects
      const intersects = raycaster.current.intersectObjects(threeScene.children, true);
      for (const hit of intersects) {
        // Particles (dust, fireflies), lines and sprites aren't things you can
        // click on — let the ray through to what's behind them.
        const ho = hit.object as any;
        if (ho.isPoints || ho.isLine || ho.isSprite) continue;
        // A portal handles its own click (travel) — don't also walk to it.
        if (ho.userData?.portal) break;
        // In the editor a click on a placed object selects it (or flashes it
        // red when it's locked); it isn't a request to walk there or sit on it.
        if ((window as any).__gxEditor && isEditorObject(hit.object)) break;
        // Clicked a marked seat (the flag lives on the content group; the ray
        // hits a child mesh, so walk up to find it). Walk there, then sit.
        let seatObj: THREE.Object3D | null = hit.object;
        while (seatObj && !seatObj.userData?.seat) seatObj = seatObj.parent;

        // Venue chairs are modelled inside the GLB, so they carry no seat flag
        // of their own — the flag lives on a small invisible marker over each
        // cushion. Clicking the visible backrest would otherwise miss it, so
        // fall back to the nearest marker to whatever was clicked.
        // Only for clicks on something raised (a cushion, a backrest): a click
        // on the floor beside a sofa is a walk, not a request to sit.
        if (!seatObj && hit.point.y > group.current.position.y + 0.15) {
          let best: THREE.Object3D | null = null;
          let bestD = 1.5;
          threeScene.traverse((o: any) => {
            if (!o.userData?.seat) return;
            const p = o.getWorldPosition(_seatProbe);
            // Same level only: measure horizontally and reject markers on
            // another floor, or a click could seat you on the wrong level. The
            // window is lopsided on purpose — a click on a tall backrest
            // (cinema recliners reach ~1.6m above the cushion marker) is still
            // that seat, while anything well below the marker is not.
            const dy = hit.point.y - p.y;
            if (dy < -0.6 || dy > 1.8) return;
            const d = Math.hypot(p.x - hit.point.x, p.z - hit.point.z);
            if (d < bestD) { bestD = d; best = o; }
          });
          seatObj = best;
        }

        if (seatObj) {
          console.log('[ClickDbg] SEAT hit', hit.object.name || hit.object.type);
          sitOn(seatObj);
          break;
        }
        // Accept ground or any mesh with a walkable surface (roughly horizontal)
        if (hit.object.name === 'ground' || hit.object.userData?.collidable) {
          console.log('[ClickDbg] WALK hit', hit.object.name || hit.object.type, '-> point', hit.point.x.toFixed(1), hit.point.z.toFixed(1), 'dist', hit.distance.toFixed(1), 'avatar at', group.current.position.x.toFixed(1), group.current.position.z.toFixed(1));
          clickTarget.current = hit.point.clone();
          clickTarget.current.y = position[1]; // Keep avatar at ground level
          clickMarkerOpacity.current = 1;
          sitTarget.current = null; // walking elsewhere cancels a pending sit
          // Cancel keyboard movement
          keys.current.w = keys.current.a = keys.current.s = keys.current.d = false;
          break;
        }
      }
    };

    // Touch support for Quest browser
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        pointerDownTime.current = performance.now();
        pointerDownPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // Only taps on the 3D view walk — not taps on HUD buttons over it.
      if (!(e.target instanceof HTMLCanvasElement)) return;
      if (e.changedTouches.length === 1) {
        const touch = e.changedTouches[0];
        const elapsed = performance.now() - pointerDownTime.current;
        const dx = touch.clientX - pointerDownPos.current.x;
        const dy = touch.clientY - pointerDownPos.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (elapsed < 400 && dist < 15) {
          handleClickToMove(touch.clientX, touch.clientY);
        }
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isMouseDown.current) {
        cameraAngle.current.x -= e.movementX * 0.01;
        cameraAngle.current.y = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, cameraAngle.current.y - e.movementY * 0.01));
      }
    };

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      cameraDistance.current = Math.max(2, Math.min(20, cameraDistance.current + e.deltaY * 0.01));
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('gathrxr:game-sit', onGameSit);
      window.removeEventListener('gathrxr:game-stand', onGameStand);
    };
  }, [isRemote, camera, threeScene, position]);

  // Update loop — Meshy animations are natively compatible, no retargeting needed
  useFrame((state, delta) => {
    if (!group.current || !mixer) return;

    // Distance/rank LOD tier for remote avatars (assigned by MultiplayerSync).
    // 'frozen'/'billboard' skip the mixer to save CPU; 'billboard' also hides
    // the skinned mesh in favour of a cheap capsule to save GPU/draw cost.
    const lod = isRemote ? (transformRef?.current?.lod ?? 'full') : 'full';
    const lodFrozen = lod === 'frozen' || lod === 'billboard';
    const lodBillboard = lod === 'billboard';
    if (isRemote) {
      if (avatarWrapper.visible === lodBillboard) avatarWrapper.visible = !lodBillboard;
      if (billboardRef.current && billboardRef.current.visible !== lodBillboard) {
        billboardRef.current.visible = lodBillboard;
      }
    }

    // Update animation mixer. While someone is actively building in VR,
    // remote avatars animate at ~10Hz to free up frame budget — accumulated
    // delta keeps playback speed correct, just chunkier.
    let mixerUpdated = true;
    if (isRemote && lodFrozen) {
      // Frozen/billboard: hold the last pose, don't sample the mixer at all.
      mixerUpdated = false;
    } else if (isRemote && useWorldStore.getState().vrBuildActive) {
      mixerThrottleAccum.current += delta;
      if (mixerThrottleAccum.current >= 0.1) {
        mixer.update(mixerThrottleAccum.current);
        mixerThrottleAccum.current = 0;
      } else {
        mixerUpdated = false;
      }
    } else {
      mixer.update(delta);
    }

    if (mixerUpdated && !actions.idle) applyStaticRestPose();

    // Own first-person view of a glTF avatar: collapse the head (VRMs use
    // their firstPerson mesh annotations instead, see VrmAvatarRuntime).
    // Only this local render — peers draw their own copy of the model.
    if (fpHead) {
      const hide = firstPerson && !disableCameraControl && !isRemote;
      if (hide) fpHead.bone.scale.setScalar(1e-4);
      else if (fpHead.hidden) fpHead.bone.scale.copy(fpHead.scale);
      fpHead.hidden = hide;
    }

    // Hover bots barely use their stub legs: damp the walk/run leg swing
    // toward the bind pose (on frames the mixer wrote, so it can't compound).
    if (hover && bonesFound.current) {
      const moving = (currentAnimation.current === 'walk' || currentAnimation.current === 'run') && !activeEmote.current;
      legDampBlend.current += ((moving ? HOVER.legDamp : 0) - legDampBlend.current) * Math.min(1, delta * 6);
      if (mixerUpdated && legDampBlend.current > 0.01) {
        const b = jumpBones.current, rest = restBoneQuats.current;
        for (const k of ['leftUpLeg', 'rightUpLeg', 'leftLeg', 'rightLeg'] as const) {
          const bone = b[k];
          if (bone && rest[k]) bone.quaternion.slerp(_legRest.copy(rest[k]), legDampBlend.current);
        }
      }
    }

    // Deep-seat sit: take last frame's hip offset out before grounding
    // (re-applied after this frame's pose, in the chair-sit pass below).
    chairFit.release();
    // Take the hover lift out before grounding, then put the new one back.
    if (hoverApplied.current) { avatarWrapper.position.y -= hoverApplied.current; hoverApplied.current = 0; }
    // Grounding re-skins every vertex on the CPU (~3 ms per 26k-vertex avatar
    // on a desktop, several times that on Quest). For other players seen from
    // a headset, ~10 Hz is plenty; per-frame cost there dropped frames and
    // made their bodies judder in VR.
    if (mixerUpdated && (!isRemote || !gl.xr.isPresenting || state.clock.elapsedTime - lastGroundAt.current > 0.1)) {
      keepAvatarOnGround();
      lastGroundAt.current = state.clock.elapsedTime;
    }
    if (hover && !(seatedOn.current && !isRemote)) {
      const t = performance.now() / 1000;
      const lift = HOVER.lift + HOVER.amp * Math.sin(t * Math.PI * 2 * HOVER.hz + hoverPhase.current);
      avatarWrapper.position.y += lift;
      hoverApplied.current = lift;
    }

    // Arm relax — Meshy's animation-library clips hold auto-rigged characters'
    // arms out too wide and stiff. Roll the upper arms toward the body on top
    // of whatever clip the mixer just wrote (mirrored signs, world-space axis).
    // Applied only on frames the mixer ran, otherwise it would accumulate.
    const canRelaxAnimatedArms = hasMeshyAnims
      && !tripoRig
      && currentAnimation.current !== '__none'
      && !!actions[currentAnimation.current];
    if (mixerUpdated && canRelaxAnimatedArms && bonesFound.current && jumpBlend.current === 0 && !activeEmote.current) {
      const angle = (window as any).__armRelax ?? ARM_RELAX_ANGLE;
      const b = jumpBones.current;
      const q = group.current.quaternion;
      if (b.leftArm) applyArmAdduction(b.leftArm, -angle, q);
      if (b.rightArm) applyArmAdduction(b.rightArm, angle, q);

      // Shoulder relax: only while idle (blended in/out to match crossfades)
      const sTarget = currentAnimation.current === 'idle' ? 1 : 0;
      shoulderBlend.current += (sTarget - shoulderBlend.current) * Math.min(1, delta * 6);
      if (shoulderBlend.current > 0.01) {
        const s = (window as any).__shoulderRelax ?? SHOULDER_RELAX;
        const t = shoulderBlend.current;
        if (b.leftShoulder) {
          rotateAboutCharacterAxis(b.leftShoulder, 0, 0, 1, -s.down * t, q);   // lower
          rotateAboutCharacterAxis(b.leftShoulder, 1, 0, 0, s.forward * t, q); // un-hunch
        }
        if (b.rightShoulder) {
          rotateAboutCharacterAxis(b.rightShoulder, 0, 0, 1, s.down * t, q);
          rotateAboutCharacterAxis(b.rightShoulder, 1, 0, 0, s.forward * t, q);
        }

        // Lift the gaze, straighten the nape, untwist the wrists, soften the
        // elbows — all scaled by the same idle blend so walking is untouched.
        const po = (window as any).__posture ?? POSTURE_RELAX;
        // Pelvis. The idle clip holds Hips at -20 deg for its whole length —
        // the groin is pushed forward and the spine curls forward to
        // compensate, a sway-back slouch. Tilt the pelvis back under the
        // torso, then counter-rotate both thighs by the same character-space
        // angle so the legs stay vertical instead of swinging out behind.
        if (po.hips && b.hips) {
          rotateAboutCharacterAxis(b.hips, 1, 0, 0, po.hips * t, q);
          if (b.leftUpLeg) rotateAboutCharacterAxis(b.leftUpLeg, 1, 0, 0, -po.hips * t, q);
          if (b.rightUpLeg) rotateAboutCharacterAxis(b.rightUpLeg, 1, 0, 0, -po.hips * t, q);
        }
        if (b.neck) rotateAboutCharacterAxis(b.neck, 1, 0, 0, po.neck * t, q);
        if (b.chest) rotateAboutCharacterAxis(b.chest, 1, 0, 0, po.chest * t, q);
        if (b.leftHand) rotateAboutCharacterAxis(b.leftHand, 0, 1, 0, po.wrist * t, q);
        if (b.rightHand) rotateAboutCharacterAxis(b.rightHand, 0, 1, 0, -po.wrist * t, q);
        if (b.leftForeArm) rotateAboutCharacterAxis(b.leftForeArm, 1, 0, 0, po.elbow * t, q);
        if (b.rightForeArm) rotateAboutCharacterAxis(b.rightForeArm, 1, 0, 0, po.elbow * t, q);
      }
    }

    if (isRemote) {
      // --- Remote avatar: lerp toward network target ---
      // Read from mutable ref if available (high-frequency, no re-renders)
      // Fall back to props for compatibility
      const tPos = transformRef?.current?.position ?? targetPosition;
      const tRot = transformRef?.current?.rotation ?? targetRotation;
      const tAnim = transformRef?.current?.animationState ?? remoteAnimationState ?? 'idle';

      if (tPos) {
        _lerpTarget.set(tPos[0], tPos[1], tPos[2]);
        // A jump of several metres is a spawn, teleport or portal, not walking:
        // place them there instead of sliding them across the room.
        if (group.current.position.distanceToSquared(_lerpTarget) > 25) {
          group.current.position.copy(_lerpTarget);
        } else {
          // Smooth interpolation — delta-based for consistent speed across frame rates
          const lerpFactor = Math.min(1, delta * 8);
          group.current.position.lerp(_lerpTarget, lerpFactor);
        }
      }
      if (tRot !== undefined) {
        const diff = tRot - group.current.rotation.y;
        const normalizedDiff = Math.atan2(Math.sin(diff), Math.cos(diff));
        const rotLerp = Math.min(1, delta * 8);
        group.current.rotation.y += normalizedDiff * rotLerp;
      }
      // Emote from the network (set by MultiplayerSync on player:emote)
      const rEmote = transformRef?.current?.emote;
      if (rEmote && (!lastRemoteEmote.current || lastRemoteEmote.current.nonce !== rEmote.nonce)) {
        lastRemoteEmote.current = { name: rEmote.name, nonce: rEmote.nonce };
        playEmote(rEmote.name);
      }
      if (!rEmote && lastRemoteEmote.current) {
        wantedEmote.current = null;
        if (activeEmote.current) actions[activeEmote.current]?.fadeOut(.15);
        activeEmote.current = null; wantedEmote.current = null;
        lastRemoteEmote.current = null;
        actions[currentAnimation.current]?.reset().fadeIn(.2).play();
      }
      // A remote that starts walking/running cancels its emote
      if (activeEmote.current && (tAnim === 'walk' || tAnim === 'run')) {
        const a = actions[activeEmote.current];
        if (a) a.fadeOut(0.15);
        activeEmote.current = null; wantedEmote.current = null;
        lastRemoteEmote.current = null;
      }

      // Movement-animation switching is suppressed while an emote plays
      if (!activeEmote.current && tAnim !== currentAnimation.current) {
        const fadeOutAction = actions[currentAnimation.current];
        const fadeInAction = actions[tAnim];
        if (fadeOutAction && fadeInAction) {
          fadeOutAction.fadeOut(0.2);
          fadeInAction.reset().fadeIn(0.2).play();
          fadeInAction.timeScale = tAnim === 'idle'
            ? (hasMeshyAnims && !hasMeshyIdle ? 0.25 : ((window as any).__idleSpeed ?? 0.65))
            : 1;
          currentAnimation.current = tAnim;
        }
      }
      return;
    }

    // --- Local avatar: keyboard + click-to-move ---

    // Phone / tablet: drag to look, pinch to zoom, joystick to move.
    const look = takeTouchLook();
    if (look.dx || look.dy) {
      if (firstPerson) {
        fpYaw.current -= look.dx * 0.005;
        fpPitch.current = Math.max(-1.4, Math.min(1.4, fpPitch.current - look.dy * 0.005));
      } else {
        cameraAngle.current.x -= look.dx * 0.008;
        cameraAngle.current.y = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, cameraAngle.current.y - look.dy * 0.008));
      }
    }
    if (look.zoom) cameraDistance.current = Math.max(2, Math.min(20, cameraDistance.current + look.zoom));
    const stick = touchMoveAmount();

    // Cancel click target if any keyboard key is pressed (or the joystick is used)
    if (keys.current.w || keys.current.s || keys.current.a || keys.current.d || stick) {
      clickTarget.current = null;
      sitTarget.current = null;
    }

    // Click-to-move: build a desired velocity toward the target (m/s).
    // _desiredVel is the velocity the avatar should have this frame; it's
    // smoothed into the actual velocity below, and integrated with delta.
    _desiredVel.set(0, 0, 0);
    let clickMoving = false;
    let clickRunning = false;
    if (clickTarget.current && group.current) {
      const dx = clickTarget.current.x - group.current.position.x;
      const dz = clickTarget.current.z - group.current.position.z;
      const distToTarget = Math.sqrt(dx * dx + dz * dz);

      // Walking to a seat: stop at the seat's EDGE (~1m) — the seat mesh is
      // collidable, so its center is physically unreachable and a 0.3m radius
      // would leave the avatar walking into it forever. The sit snap below
      // places the avatar exactly on the seat.
      const arriveRadius = sitTarget.current ? 1.0 : 0.3;
      // Click-to-walk is a straight line, so a seat boxed in by solid
      // furniture (a host chair behind a desk, the middle of a bench row)
      // can't be reached that close. If the walk stops making progress while
      // the seat is near, treat it as arrived — the sit snap places the
      // avatar on the seat regardless of where it stands.
      let stalledNearSeat = false;
      if (sitTarget.current) {
        const now = performance.now();
        if (distToTarget < seatApproach.current.best - 0.05) {
          seatApproach.current = { best: distToTarget, t: now };
        } else if (now - seatApproach.current.t > 400 && distToTarget < 3.5) {
          stalledNearSeat = true;
        }
      }
      if (distToTarget < arriveRadius || stalledNearSeat) {
        // Arrived at target
        clickTarget.current = null;
      } else {
        clickMoving = true;
        clickRunning = distToTarget > 5; // Run if far away
        const moveSpeed = clickRunning ? 10 : 5;
        _desiredVel.set(dx, 0, dz).normalize().multiplyScalar(moveSpeed);

        // Face movement direction (smoothed below via targetYaw)
        targetYaw.current = Math.atan2(dx, dz);
      }
    }

    // Fade click marker
    if (clickMarkerRef.current) {
      if (clickTarget.current) {
        clickMarkerRef.current.position.copy(clickTarget.current);
        clickMarkerRef.current.position.y = position[1] + 0.05;
        clickMarkerRef.current.visible = true;
        clickMarkerOpacity.current = Math.max(clickMarkerOpacity.current - delta * 0.5, 0.3);
        (clickMarkerRef.current.material as THREE.MeshBasicMaterial).opacity = clickMarkerOpacity.current;
        clickMarkerRef.current.rotation.y += delta * 2;
      } else {
        clickMarkerOpacity.current = Math.max(clickMarkerOpacity.current - delta * 3, 0);
        (clickMarkerRef.current.material as THREE.MeshBasicMaterial).opacity = clickMarkerOpacity.current;
        if (clickMarkerOpacity.current <= 0) clickMarkerRef.current.visible = false;
      }
    }

    // Joystick pushed all the way = run; partway = walk at that fraction.
    const stickRun = stick > 0.92;
    const speed = keys.current.shift || stickRun ? 10 : stick ? 5 * Math.max(0.35, stick) : 5;
    const isMoving = keys.current.w || keys.current.s || keys.current.a || keys.current.d || clickMoving || !!stick;
    const isRunning = (isMoving && (keys.current.shift || stickRun)) || clickRunning;

    // An active emote is cancelled the moment the player actually moves (WASD,
    // click-to-move, or any real velocity). Dances stop when you walk away.
    const movingNow = isMoving || !!clickTarget.current
      || Math.abs(velocity.current.x) > 0.05 || Math.abs(velocity.current.z) > 0.05;
    if (movingNow) hasMoved.current = true; // stop re-applying spawn position
    if (activeEmote.current && movingNow) {
      const a = actions[activeEmote.current];
      if (a) a.fadeOut(0.15);
      activeEmote.current = null; wantedEmote.current = null;
      currentAnimation.current = '__none'; // force the crossfade below to re-fade in movement
      useWorldStore.getState().stopLocalEmote();
    }

    // Safety: a pending sit that hasn't completed within 8s (pathing blocked,
    // seat unreachable) is dropped so the avatar never walks/waits forever.
    if (sitTarget.current && performance.now() - sitTarget.current.t > 8000) {
      sitTarget.current = null;
      clickTarget.current = null;
    }

    // TEMP debug: 1/s position while walking or seated (local only)
    if (!isRemote && group.current && (clickTarget.current || SIT_EMOTES.has(activeEmote.current ?? ''))) {
      const nowD = performance.now();
      if (!(window as any).__posDbgT || nowD - (window as any).__posDbgT > 1000) {
        (window as any).__posDbgT = nowD;
        console.log('[PosDbg]', clickTarget.current ? 'walking' : 'seated', 'at', group.current.position.x.toFixed(1), group.current.position.y.toFixed(1), group.current.position.z.toFixed(1), '| cam', camera.position.x.toFixed(1), camera.position.y.toFixed(1), camera.position.z.toFixed(1));
      }
    }

    // Reached a seat: once we've arrived (clickTarget cleared) and settled
    // (no residual velocity, so the emote-cancel guard above won't fire),
    // snap exactly onto the seat facing the fire and play the Sit emote.
    if (sitTarget.current && !clickTarget.current && !activeEmote.current && group.current
        && Math.abs(velocity.current.x) < 0.05 && Math.abs(velocity.current.z) < 0.05) {
      // Judge height from where the avatar actually ended up, not where it
      // was when the seat was clicked: in a venue with a stage or mezzanine
      // the click often comes from another level, and clamping to that height
      // left the avatar sitting in mid-air over the sofa.
      // The seat's own height sets the floor for the snap (a chair's root sits
      // half a metre under its cushion), so stopping on a stage edge or step
      // beside the sofa no longer holds the avatar up at that height.
      const feet = group.current.position.y;
      const surface = sitTarget.current.surface;
      sitTarget.current.minY = Math.min(feet, surface - 0.55);
      sitTarget.current.floor = feet;
      sitTarget.current.maxY = feet + 6;
      // A seat on another floor (a sofa on the mezzanine above you, or below
      // you while you stand on it) can't be reached by walking — don't jump
      // there, just stop. Theatre tiers stay within reach.
      const rise = surface - feet;
      if (rise > 2.0 || rise < -1.0) sitTarget.current = null;
    }
    if (sitTarget.current && !clickTarget.current && !activeEmote.current && group.current
        && Math.abs(velocity.current.x) < 0.05 && Math.abs(velocity.current.z) < 0.05) {
      // Sit in the player's chosen style (chair / floor / squat).
      const { t: _t, ...seat } = sitTarget.current;
      // Measure this avatar's standing hip height now, while it's still
      // standing — floor/squat placement scales with it.
      const hipsBone = jumpBones.current.hips;
      if (hipsBone) {
        const hy = hipsBone.getWorldPosition(new THREE.Vector3()).y - group.current.position.y;
        if (hy > 0.2 && hy < 2) seat.hipH = hy; // kid-height bots have ~0.3 m hips
      }
      const style = getSitStyle();
      placeOnSeat(seat, style.emote);
      seatedOn.current = seat;
      sitTarget.current = null;
      // Seat height is owned by the sit snap from here on — claim it in the
      // same frame so terrain-follow can't drag the avatar down a tier while
      // the emote is still starting.
      activeEmote.current = style.emote;
      const wid = useWorldStore.getState().actualWorldId || getCurrentWorldId();
      useWorldStore.getState().playLocalEmote(style.emote);
      if (wid) broadcastEmote(wid, style.emote);
    }

    // Determine target animation
    let targetAnimation = 'idle';
    if (isRunning && actions.run) {
      targetAnimation = 'run';
    } else if (isMoving && actions.walk) {
      targetAnimation = 'walk';
    }

    // Switch animations with crossfade — suppressed while an emote is playing so
    // it isn't overridden by idle/walk/run.
    if (!activeEmote.current && targetAnimation !== currentAnimation.current) {
      const fadeOutAction = actions[currentAnimation.current];
      const fadeInAction = actions[targetAnimation];

      if (fadeInAction) {
        if (fadeOutAction) fadeOutAction.fadeOut(0.2);
        fadeInAction.reset().fadeIn(0.2).play();
        // Idle is slowed-down walk when using Meshy anims
        fadeInAction.timeScale = targetAnimation === 'idle'
          ? (hasMeshyAnims && !hasMeshyIdle ? 0.25 : ((window as any).__idleSpeed ?? 0.65))
          : 1;
        currentAnimation.current = targetAnimation;
      }
    }

    // Movement (camera-relative)
    _forward.set(0, 0, 0);
    _right.set(0, 0, 0);

    if (keys.current.w) _forward.z = -1;
    if (keys.current.s) _forward.z = 1;
    if (keys.current.a) _right.x = -1;
    if (keys.current.d) _right.x = 1;
    if (stick && !_forward.z && !_right.x) { _forward.z = -touchMove.z; _right.x = touchMove.x; }

    // Rotate movement based on camera angle.
    // In first-person, fpYaw is the camera yaw (same convention as cameraAngle.x).
    const moveAngle = firstPerson ? fpYaw.current : cameraAngle.current.x;
    direction.current.set(
      _forward.z * Math.sin(moveAngle) + _right.x * Math.cos(moveAngle),
      0,
      _forward.z * Math.cos(moveAngle) - _right.x * Math.sin(moveAngle)
    );

    // Keyboard input wins over click-to-move for the desired velocity.
    if (direction.current.lengthSq() > 0) {
      direction.current.normalize();
      _desiredVel.copy(direction.current).multiplyScalar(speed);
      // Third-person: face movement direction (smoothed below)
      if (!firstPerson) {
        targetYaw.current = Math.atan2(direction.current.x, direction.current.z);
      }
    }

    // Smooth actual velocity toward the desired velocity. Frame-rate
    // independent: the same easing rate regardless of FPS. desiredVel is 0
    // when idle, so this also handles deceleration (no separate damping).
    const accelFactor = smoothFactor(14, delta);
    velocity.current.x += (_desiredVel.x - velocity.current.x) * accelFactor;
    velocity.current.z += (_desiredVel.z - velocity.current.z) * accelFactor;
    // Clamp tiny velocities to zero (prevents drift)
    if (Math.abs(velocity.current.x) < 0.002) velocity.current.x = 0;
    if (Math.abs(velocity.current.z) < 0.002) velocity.current.z = 0;

    // Smoothly rotate the avatar toward its target facing.
    if (firstPerson) {
      // First-person: avatar always faces where the camera looks. The camera
      // looks down its -Z, the model faces +Z: turn the body by PI (it used
      // to equal fpYaw, so the body — and what peers saw — faced backwards).
      group.current.rotation.y = fpYaw.current + Math.PI;
      targetYaw.current = group.current.rotation.y;
      yawInit.current = true;
    } else if (!yawInit.current) {
      group.current.rotation.y = targetYaw.current;
      yawInit.current = true;
    } else {
      group.current.rotation.y = lerpAngle(group.current.rotation.y, targetYaw.current, smoothFactor(12, delta));
    }

    // Refresh collidable cache every 500ms (not every frame) — but every
    // 100ms while it is still empty: the environment usually finishes loading
    // after the avatar, and until the cache sees its walls the orbit camera
    // has nothing to collide with and shows the outside of the room. (Not
    // every frame: a world with no collidables at all stays empty forever.)
    const now = performance.now();
    if (now - lastCacheTime.current > (collidableCache.current.length === 0 ? 100 : 500)) {
      collidableCache.current = [];
      slabCache.current = [];
      // Only what is near enough to matter before the next rebuild. At walking
      // speed the avatar covers ~2m in 500ms, so a 30m radius is generous.
      const NEAR = 30;
      const ax = group.current.position.x;
      const az = group.current.position.z;
      threeScene.traverse((obj) => {
        if (!(obj as THREE.Mesh).isMesh) return;
        if (obj.userData?.collidable !== true) {
          // Camera-only slabs: primitive boxes/planes only (cheap type check
          // first, so a big GLB's meshes are never measured here).
          const gt = (obj as THREE.Mesh).geometry?.type;
          if ((gt !== 'BoxGeometry' && gt !== 'PlaneGeometry') || !obj.visible) return;
          const sb = new THREE.Box3().setFromObject(obj);
          if (sb.isEmpty() || !isSlab(sb)) return;
          if (sb.max.x < ax - NEAR || sb.min.x > ax + NEAR ||
              sb.max.z < az - NEAR || sb.min.z > az + NEAR) return;
          slabCache.current.push(sb);
          return;
        }
        const box = new THREE.Box3().setFromObject(obj);
        if (box.isEmpty()) return;
        if (box.max.x < ax - NEAR || box.min.x > ax + NEAR ||
            box.max.z < az - NEAR || box.min.z > az + NEAR) return;
        collidableCache.current.push({ mesh: obj as THREE.Mesh, box });
      });
      lastCacheTime.current = now;
    }

    // Collision: resolve overlaps + block movement into collidables.
    // Work in this frame's displacement (velocity * delta), so behavior is
    // frame-rate independent.
    // Suspended while seated: the sit snap intentionally places the avatar ON
    // the (collidable) seat — the push-out would otherwise shove the seated
    // avatar sideways at 3 m/s forever (drifting them out of the world).
    // Movement cancels the emote earlier in this frame, so collision resumes
    // the moment the player stands up.
    const seated = SIT_EMOTES.has(activeEmote.current ?? '');
    // Stood up (moved, jumped, picked another emote): forget the seat.
    if (!seated) seatedOn.current = null;
    const avatarRadius = 0.4;
    // Max ledge height the avatar auto-climbs (stair risers are 0.25). Anything
    // with its top within reach becomes walkable terrain instead of a blocker.
    const STEP_UP = 0.55;
    const posX = group.current.position.x;
    const posZ = group.current.position.z;
    const feetY = group.current.position.y;
    const curY = feetY + 0.5;
    // A long frame (GC, texture upload) must not carry the avatar clean
    // through a wall in one step: cap the step's time at 0.1 s.
    const stepDt = Math.min(delta, 0.1);
    let dispX = velocity.current.x * stepDt;
    let dispZ = velocity.current.z * stepDt;
    let blockedX = false;
    let blockedZ = false;
    // Height of whatever the avatar is standing on.
    //
    // The world ground is only the fallback. Seeding support with it outright
    // put an invisible floor at spawn height across the entire world, so a
    // staircase leading DOWN was unwalkable: every tread below spawn lost to
    // the seed and the avatar strode off the top step into open air. When
    // something collidable is underfoot it defines the floor — above or below
    // spawn — and the ground only catches the avatar where nothing else does.
    let support = position[1];
    let colliderTop = -Infinity;

    for (const entry of collidableCache.current) {
      if (seated) break;
      _colBox.copy(entry.box);

      // Terrain support: the highest climbable top under the avatar's center.
      if (posX > _colBox.min.x - 0.05 && posX < _colBox.max.x + 0.05 &&
          posZ > _colBox.min.z - 0.05 && posZ < _colBox.max.z + 0.05) {
        const top = _colBox.max.y;
        if (top <= feetY + STEP_UP && top > colliderTop) colliderTop = top;
      }

      // Climbable ledges never block movement — the avatar steps up onto them.
      if (_colBox.max.y <= feetY + STEP_UP) continue;

      // Skip if no Y overlap
      if (curY < _colBox.min.y || curY > _colBox.max.y) continue;

      // Expand box by avatar radius
      const minX = _colBox.min.x - avatarRadius;
      const maxX = _colBox.max.x + avatarRadius;
      const minZ = _colBox.min.z - avatarRadius;
      const maxZ = _colBox.max.z + avatarRadius;

      // Push-out: if already overlapping, nudge player out (prevents getting stuck)
      if (posX > minX && posX < maxX && posZ > minZ && posZ < maxZ) {
        const escapeLeft = posX - minX;
        const escapeRight = maxX - posX;
        const escapeBack = posZ - minZ;
        const escapeFront = maxZ - posZ;
        const minEscape = Math.min(escapeLeft, escapeRight, escapeBack, escapeFront);

        // Standing well inside the box means we're within a HOLLOW structure —
        // a venue, a room, a building interior — not wedged inside solid
        // geometry. An axis-aligned box can't describe "hollow", so the only
        // safe move is to ignore this mesh: pushing out would eject the player
        // through the nearest wall, and per-axis blocking would freeze them in
        // place (every direction still lands inside the box). Real solids are
        // thin enough that being inside one always leaves a short escape.
        if (minEscape > avatarRadius + 0.6) continue;

        const push = 3 * delta; // ~3 m/s escape, frame-rate independent

        if (minEscape === escapeLeft) dispX -= push;
        else if (minEscape === escapeRight) dispX += push;
        else if (minEscape === escapeBack) dispZ -= push;
        else dispZ += push;

        clickTarget.current = null; // stop walking into the wall
        continue;
      }

      // Block movement per-axis (allows wall sliding)
      if (posX + dispX > minX && posX + dispX < maxX && posZ > minZ && posZ < maxZ) {
        dispX = 0;
        blockedX = true;
      }
      if (posX > minX && posX < maxX && posZ + dispZ > minZ && posZ + dispZ < maxZ) {
        dispZ = 0;
        blockedZ = true;
      }
    }

    if (colliderTop > -Infinity) support = colliderTop;

    // Kill velocity on blocked axes so it doesn't build up against the wall
    if (blockedX) velocity.current.x = 0;
    if (blockedZ) velocity.current.z = 0;

    // Update position
    group.current.position.x += dispX;
    group.current.position.z += dispZ;

    // Walkable bounds (settings.bounds): walking, the phone joystick and
    // click-to-move all stop at the room's edge — even where it has no wall.
    const bounds = seated ? null : useWorldStore.getState().worldBounds;
    if (bounds && !insideBounds(bounds, group.current.position.x, group.current.position.z)) {
      const [bx, bz] = clampToBounds(bounds, group.current.position.x, group.current.position.z);
      if (bx !== group.current.position.x) velocity.current.x = 0;
      if (bz !== group.current.position.z) velocity.current.z = 0;
      group.current.position.x = bx;
      group.current.position.z = bz;
      clickTarget.current = null; // a target outside can never be reached
    }

    // Vertical terrain-follow: ease up onto stairs/ledges, fall off edges at a
    // capped rate. Jump physics owns Y while airborne; the seat snap owns it
    // while seated.
    if (!isJumping.current && !seated) {
      const dy = support - group.current.position.y;
      if (Math.abs(dy) < 0.002) group.current.position.y = support;
      else if (dy > 0) group.current.position.y += dy * smoothFactor(14, delta);
      else group.current.position.y = Math.max(support, group.current.position.y - 10 * delta);
    }

    // Jump physics — lands on the current support (stair steps, floors, wall
    // tops), not just the world ground.
    const groundY = support;
    if (isJumping.current) {
      jumpVelocity.current -= 20 * delta; // Gravity
      group.current.position.y += jumpVelocity.current * delta;
      if (group.current.position.y <= groundY) {
        group.current.position.y = groundY;
        isJumping.current = false;
        jumpVelocity.current = 0;
        // Landing: crossfade back from jump to current movement animation
        if (hasMeshyJump && actions.jump) {
          actions.jump.fadeOut(0.2);
          const resumeAnim = actions[currentAnimation.current];
          if (resumeAnim) resumeAnim.reset().fadeIn(0.2).play();
        }
      }
    } else if (!seated) {
      // Not while seated: the sit snap owns Y, and with collision suspended
      // `support` falls back to the world ground — this line used to drop
      // every seated avatar to floor level (into the couch, or through a
      // mezzanine to the floor below).
      group.current.position.y = groundY;
    }

    // Procedural jump pose fallback (only when no Meshy jump animation)
    if (!hasMeshyJump) {
      const targetBlend = isJumping.current ? 1 : 0;
      jumpBlend.current += (targetBlend - jumpBlend.current) * (isJumping.current ? 0.15 : 0.1);
      if (jumpBlend.current < 0.01) jumpBlend.current = 0;

      if (jumpBlend.current > 0 && bonesFound.current) {
        const b = jumpBones.current;
        const t = jumpBlend.current;

        if (b.leftArm) b.leftArm.rotation.x -= t * 1.2;
        if (b.rightArm) b.rightArm.rotation.x -= t * 1.2;
        if (b.leftForeArm) b.leftForeArm.rotation.x -= t * 0.4;
        if (b.rightForeArm) b.rightForeArm.rotation.x -= t * 0.4;
        if (b.leftUpLeg) b.leftUpLeg.rotation.x -= t * 0.7;
        if (b.rightUpLeg) b.rightUpLeg.rotation.x -= t * 0.7;
        if (b.leftLeg) b.leftLeg.rotation.x -= t * 0.5;
        if (b.rightLeg) b.rightLeg.rotation.x -= t * 0.5;
        if (b.spine) b.spine.rotation.x += t * 0.15;
      }
    }

    // Camera follow (only if not disabled)
    if (!disableCameraControl) {
      if (firstPerson) {
        // First-person: camera sits at eye level inside avatar head.
        // Rotation is set from pointer-lock mouse look (fpYaw / fpPitch).
        camera.rotation.order = 'YXZ';
        camera.rotation.y = fpYaw.current;
        camera.rotation.x = fpPitch.current;
        // No roll. The third-person lookAt leaves a z from its XYZ-order
        // decomposition (up to PI when looking toward +Z); keeping it under
        // YXZ order tilted — or flipped — the first-person view.
        camera.rotation.z = 0;
        // Eyes sit a little forward of the neck (the head is hidden), more so
        // when looking down, so you see your chest and feet from above
        // instead of the inside of the torso.
        const h = targetHeight ?? 1.75;
        const fwd = h * (0.06 + 0.04 * Math.max(0, -fpPitch.current) / 1.4);
        // At a game table a short avatar's eye would sit level with the
        // felt: keep it TABLE_EYE above the tabletop, like VR does.
        const tableTop = seatedOn.current?.tableTop;
        const eyeY = group.current.position.y + eyeHeight(targetHeight);
        camera.position.set(
          group.current.position.x - Math.sin(fpYaw.current) * fwd,
          tableTop != null ? Math.max(eyeY, tableTop + TABLE_EYE) : eyeY,
          group.current.position.z - Math.cos(fpYaw.current) * fwd
        );
      } else {
        // Third-person orbit
        const dist = cameraDistance.current;
        const baseHeight = Math.max(1.5, dist * 0.5);
        _camOffset.set(
          Math.sin(cameraAngle.current.x) * dist,
          baseHeight + Math.sin(cameraAngle.current.y) * dist * 0.5,
          Math.cos(cameraAngle.current.x) * dist
        );
        _lookTarget.set(
          group.current.position.x,
          group.current.position.y + lookHeight(targetHeight),
          group.current.position.z
        );

        // Camera collision ("spring arm", utils/cameraArm): never let the
        // orbit camera leave the space the avatar is in. Cast from the head
        // toward the desired camera spot and stop ARM_MARGIN short of the
        // first blocker — walls, ceilings, floors. Hidden authored proxies
        // (collider_*) are tested as their boxes (cheap, winding-agnostic);
        // visible collidables by their real triangles (BVH), so a hollow
        // building without proxies still stops the camera at its wall rather
        // than its outer bounding box. Colliders topping out below the head
        // (tables, seats, low furniture proxies) are skipped: the camera always
        // sits above the head, they could only make it jitter. Wide slabs
        // overhead that aren't flagged collidable (a primitive ceiling) block
        // too — without them the arm, which rises with distance, cleared the
        // walls' tops and passed straight through the Game Room's ceiling.
        const headY = _lookTarget.y;
        const wanted = Math.hypot(_camOffset.x, _camOffset.y, _camOffset.z);
        let allowed = wanted;
        _camRay.origin.copy(_lookTarget);
        _camRay.direction.copy(_camOffset).normalize();
        _camCaster.set(_lookTarget, _camRay.direction);
        // Cheapest lowest ceiling over the avatar, for the final clamp.
        let ceilingY = Infinity;
        const ax = _lookTarget.x, az = _lookTarget.z;
        for (const entry of collidableCache.current) {
          const b = entry.box;
          if (!blocksCamera(b, headY)) continue;
          if (isOverhead(b, headY) && isSlab(b) && coversXZ(b, ax, az) && b.min.y < ceilingY) ceilingY = b.min.y;
          // Box can't be reached by the arm at all: skip the triangle test.
          const boxD = rayBoxDistance(_lookTarget, _camRay.direction, b);
          if (boxD >= allowed + ARM_MARGIN) continue;
          let d = boxD;
          if (entry.mesh.visible) {
            _camCaster.far = allowed + ARM_MARGIN;
            const hit = _camCaster.intersectObject(entry.mesh, false)[0];
            d = hit ? hit.distance : Infinity;
          }
          allowed = shortenArm(allowed, d);
        }
        for (const b of slabCache.current) {
          if (!isOverhead(b, headY)) continue; // floors, rugs, stage carpets
          if (coversXZ(b, ax, az) && b.min.y < ceilingY) ceilingY = b.min.y;
          allowed = shortenArm(allowed, rayBoxDistance(_lookTarget, _camRay.direction, b));
        }
        // Walkable bounds act as invisible walls for the arm — but only in a
        // built space (anything to collide with): open worlds with nothing
        // collidable are left exactly as they were.
        const enclosed = collidableCache.current.length > 0 || slabCache.current.length > 0;
        if (enclosed) allowed = Math.min(allowed, rayExitBounds(_lookTarget, _camRay.direction, useWorldStore.getState().worldBounds));
        allowed = Math.max(ARM_MIN, allowed);
        _camDesired.copy(_lookTarget).addScaledVector(_camRay.direction, allowed);

        // The stand-off above is measured along the ray, so at a grazing
        // angle the camera can still end up a few cm from a wall and the view
        // frustum pokes through it. Back off until the camera itself is
        // clear of every hidden proxy box and overhead slab by CLEAR in all
        // directions.
        const CLEAR = 0.3;
        for (let guard = 0; guard < 40 && allowed > ARM_MIN; guard++) {
          let inside = false;
          for (const entry of collidableCache.current) {
            if (entry.mesh.visible) continue;
            const b = entry.box;
            if (!blocksCamera(b, headY)) continue;
            if (nearBox(_camDesired, b, CLEAR) &&
                // a box the head is already inside (low ceiling overhead, the
                // floor slab) can't be escaped by pulling in — ignore it here
                !(_lookTarget.x > b.min.x && _lookTarget.x < b.max.x &&
                  _lookTarget.y > b.min.y - CLEAR && _lookTarget.y < b.max.y + CLEAR &&
                  _lookTarget.z > b.min.z && _lookTarget.z < b.max.z)) {
              inside = true;
              break;
            }
          }
          if (!inside) {
            for (const b of slabCache.current) {
              if (isOverhead(b, headY) && b.min.y - headY > CLEAR && nearBox(_camDesired, b, CLEAR)) { inside = true; break; }
            }
          }
          if (!inside) break;
          allowed = Math.max(ARM_MIN, allowed - 0.1);
          _camDesired.copy(_lookTarget).addScaledVector(_camRay.direction, allowed);
        }

        // Final clamp: above the floor, under the ceiling found above.
        const camClamp = _camClamp;
        camClamp.floorY = group.current.position.y + FLOOR_CLEARANCE;
        camClamp.ceilingY = ceilingY === Infinity ? Infinity : Math.max(ceilingY - CLEAR, headY + 0.2);
        const clamped = clampCamera(_camDesired, camClamp);

        // Pull in instantly when something is in the way (so the outside of
        // the room is never shown); ease back out once it clears. Ease is
        // frame-rate independent (was a fixed 0.1 per frame → jittery at high
        // FPS, laggy at low FPS).
        const obstructed = clamped || allowed < wanted - 0.01;
        if (obstructed && camera.position.distanceTo(_lookTarget) > _camDesired.distanceTo(_lookTarget) + 0.05) {
          camera.position.copy(_camDesired);
        } else {
          camera.position.lerp(_camDesired, smoothFactor(10, delta));
          clampCamera(camera.position, camClamp);
        }

        camera.lookAt(_lookTarget);
      }
    }
  });

  // Deep-seat sit (markers with seatFit: 'deep', e.g. the conference pods):
  // after the sit clip has posed the body, rest the hips on the cushion and
  // lay the legs over the seat's front edge, measured from
  // this avatar's own legs (avatar/chairSit.ts). Remote copies find the same
  // marker from where the player is, so a VR player — whose rig sits by a
  // fixed drop — looks seated the same way to everyone else. Runs after the
  // main frame so the remote lerp and local seat snap have already moved
  // the root this frame.
  useFrame(() => {
    const g = group.current;
    if (!g || !chairFit.usable) return;
    if (activeEmote.current !== 'sit' || !avatarWrapper.visible) {
      chairSeat.current = null;
      sitSettledAt.current = null;
      if (!SIT_EMOTES.has(activeEmote.current ?? '')) standY.current = g.position.y;
      return;
    }
    let found = chairSeat.current;
    if (!isRemote) {
      const on = seatedOn.current;
      if (!on || on.fit !== 'chair') return;
      if (!found || found.x !== on.x || found.z !== on.z) {
        const marker = findChairMarker(threeScene, on.x, g.position.y, on.z, 0.1);
        const seat = marker ? chairSeatFromMarker(marker) : null;
        found = chairSeat.current = seat ? { x: on.x, z: on.z, seat, floor: on.floor ?? null, at: performance.now() } : null;
      }
    } else {
      // Where the player says they are (not the lerped body), re-checked when
      // that moves or every couple of seconds (markers can load late).
      const t = transformRef?.current?.position ?? targetPosition;
      const [px, py, pz] = t ?? [g.position.x, g.position.y, g.position.z];
      if (!found || Math.hypot(found.x - px, found.z - pz) > 0.25 || performance.now() - found.at > 2000) {
        const marker = findChairMarker(threeScene, px, py, pz);
        const seat = marker ? chairSeatFromMarker(marker) : null;
        found = chairSeat.current = seat ? { x: px, z: pz, seat, floor: standY.current, at: performance.now() } : null;
      }
      if (found) g.rotation.y = found.seat.yaw;
    }
    // Measured once the sit clip has fully replaced the standing pose (the
    // outgoing idle fades for a moment after the sit reaches full weight).
    const now = performance.now();
    if ((actions.sit?.getEffectiveWeight() ?? 1) < 0.999) sitSettledAt.current = null;
    else sitSettledAt.current ??= now;
    if (found) chairFit.apply(found.seat, found.floor, sitSettledAt.current != null && now - sitSettledAt.current > 600);
  });

  return (
    <>
      <group ref={group}>
        <primitive object={avatarWrapper} scale={[1, 1, 1]} />
        {/* Far-distance LOD proxy — a cheap capsule shown instead of the
            skinned avatar when this remote player is billboarded (toggled in
            the frame loop). Never rendered for the local avatar. */}
        {isRemote && (
          <mesh ref={billboardRef} position={[0, 1, 0]} visible={false}>
            <capsuleGeometry args={[0.3, 1, 4, 8]} />
            <meshStandardMaterial color="#00c2b3" transparent opacity={0.75} />
          </mesh>
        )}
        {/* Name tag attached to the avatar */}
        {userName && showNameTag && (
          <FloatingNameTagWithEvents
            name={userName}
            position={[0, 0, 0]}
            height={nameTagHeight(targetHeight)}
          />
        )}
        {avatar.vrm && (
          <VrmAvatarRuntime
            vrm={avatar.vrm}
            isRemote={isRemote}
            voiceUserId={voiceUserId}
            firstPerson={firstPerson && !disableCameraControl}
            lodRef={transformRef}
            seed={`${userName || ''}|${avatarUrl}`}
          />
        )}
      </group>
      {/* Click-to-move target marker */}
      {!isRemote && (
        <mesh ref={clickMarkerRef} rotation={[-Math.PI / 2, 0, 0]} visible={false}>
          <ringGeometry args={[0.3, 0.5, 32]} />
          <meshBasicMaterial color="#00c2b3" transparent opacity={0.6} depthWrite={false} side={THREE.DoubleSide} />
        </mesh>
      )}
    </>
  );
};

// Preload Mixamo fallbacks (used when no Meshy animations are available)
useGLTF.preload('/animations/idle-male.glb');
useGLTF.preload('/animations/walk-male.glb');
useGLTF.preload('/animations/run-male.glb');

export default FixedRetargetedAvatar;
